import { useEffect, useRef, useState } from 'react';

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('es-MX', { hour12: false });
}

function rssiColor(rssi) {
  if (rssi == null || isNaN(rssi)) return '#8b96ad';
  if (rssi >= -70) return '#34d399';
  if (rssi >= -90) return '#38bdf8';
  if (rssi >= -110) return '#fbbf24';
  return '#f87171';
}

function rssiLabel(rssi) {
  if (rssi == null || isNaN(rssi)) return '—';
  if (rssi >= -70) return 'Excelente';
  if (rssi >= -90) return 'Buena';
  if (rssi >= -110) return 'Débil';
  return 'Crítica';
}

export default function Lora({ devices, authHeaders, socketRef, initialDevice = '', onRegister = null }) {
  const [device, setDevice] = useState(initialDevice || '');
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const chartRef = useRef(null);
  const chartInstRef = useRef(null);

  async function load(dev) {
    const id = dev !== undefined ? dev : device;
    if (!id) { setLatest(null); setHistory([]); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/lora?deviceId=' + encodeURIComponent(id) + '&limit=300', { headers: authHeaders() });
      const d = await res.json();
      setLatest(d.latest || null);
      setHistory(Array.isArray(d.history) ? d.history : []);
    } catch (e) {
      console.error('lora', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (initialDevice) { setDevice(initialDevice); load(initialDevice); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDevice]);

  useEffect(() => {
    const socket = socketRef && socketRef.current;
    if (!socket) return;
    const handler = (s) => {
      if (!s || s.deviceId !== device) return;
      const point = { t: s.receivedAt, gw: s.gw, rssi: s.rssi, snr: s.snr, data: s.data };
      setLatest(point);
      setHistory((prev) => [...prev.slice(-299), point]);
    };
    socket.on('lora-data', handler);
    return () => { socket.off('lora-data', handler); };
  }, [device, socketRef]);

  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas || !history.length) return;
    if (chartInstRef.current) chartInstRef.current.destroy();
    const light = document.documentElement.dataset.theme === 'light';
    const tc = light ? '#1a2233' : '#e6ebf5';
    const gc = light ? '#d9e0ea' : '#263047';
    chartInstRef.current = new Chart(canvas, {
      type: 'line',
      data: {
        labels: history.map((p) => (p.t || '').slice(11, 16)),
        datasets: [
          { label: 'RSSI (dBm)', data: history.map((p) => p.rssi), borderColor: '#38bdf8', fill: false, tension: 0.3, spanGaps: true, yAxisID: 'y' },
          { label: 'SNR (dB)', data: history.map((p) => p.snr), borderColor: '#34d399', fill: false, tension: 0.3, spanGaps: true, yAxisID: 'y2' },
        ],
      },
      options: {
        responsive: true,
        animation: false,
        plugins: { legend: { labels: { color: tc } } },
        scales: {
          x: { ticks: { color: tc }, grid: { color: gc } },
          y: { ticks: { color: tc }, grid: { color: gc } },
          y2: { position: 'right', ticks: { color: tc }, grid: { drawOnChartArea: false } },
        },
      },
    });
    return () => { if (chartInstRef.current) { chartInstRef.current.destroy(); chartInstRef.current = null; } };
  }, [history]);

  function dataEntries() {
    const d = latest && latest.data;
    if (!d || typeof d !== 'object') return [];
    return Object.entries(d).filter(([k]) => k !== 'n' && k !== 't');
  }

  return (
    <div className="manual">
      <section className="card">
        <div className="panel-head">
          <h2>Red LoRa</h2>
          {onRegister && (
            <button className="mini add" onClick={onRegister} title="Dar de alta nodo/gateway LoRa">+</button>
          )}
        </div>
        <div className="cmd-form">
          <select value={device} onChange={(e) => { setDevice(e.target.value); load(e.target.value); }}>
            <option value="">— Nodo LoRa —</option>
            {devices.map((d) => (
              <option key={d.uniqueid || d.id} value={d.uniqueid || d.id}>{d.uniqueid || d.name}</option>
            ))}
          </select>
          <button className="mini icon" onClick={() => load()} disabled={loading || !device} title="Refrescar">⟳</button>
        </div>
      </section>

      {!device && (
        <section className="card"><div className="empty">Selecciona un nodo LoRa para ver RSSI, SNR y telemetría.</div></section>
      )}

      {device && (
        <>
          <section className="stats">
            <div className="card stat"><div className="stat-value" style={{ color: rssiColor(latest && latest.rssi) }}>{latest && latest.rssi != null ? latest.rssi + ' dBm' : '—'}</div><div className="stat-label">RSSI ({rssiLabel(latest && latest.rssi)})</div></div>
            <div className="card stat"><div className="stat-value">{latest && latest.snr != null ? latest.snr + ' dB' : '—'}</div><div className="stat-label">SNR</div></div>
            <div className="card stat"><div className="stat-value mono">{latest && latest.gw ? latest.gw : '—'}</div><div className="stat-label">Gateway</div></div>
            <div className="card stat"><div className="stat-value">{latest ? fmtTime(latest.t) : '—'}</div><div className="stat-label">Última recepción</div></div>
          </section>

          <section className="card panel">
            <h2>Últimos datos del nodo</h2>
            <table>
              <thead><tr><th>Campo</th><th>Valor</th></tr></thead>
              <tbody>
                {dataEntries().length === 0 && <tr><td colSpan={2} className="empty">Sin datos aún</td></tr>}
                {dataEntries().map(([k, v]) => (
                  <tr key={k}><td className="mono">{k}</td><td>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</td></tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="card panel">
            <h2>Calidad de enlace (RSSI / SNR)</h2>
            {history.length === 0 && <div className="empty">Aún sin lecturas</div>}
            <div style={{ height: 280 }}><canvas ref={chartRef} /></div>
          </section>
        </>
      )}
    </div>
  );
}
